import {
    IApprovalDescriptor,
    IDataRequest,
    IDataRequestPermissionsHelpers,
    IRequestedStudy,
    IResearchTeamMemberDetail,
} from '@vivli/features/data-requests/infrastructure/interface';
import { ApprovalStatusEnum, DataRequestStatusEnum, StudyRequestStatusEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { IOrganizationRoles, IUser } from '@vivli/shared/infrastructure/interface';
import { PermissionsEnum } from '@vivli/shared/infrastructure/enum';

interface IDataRequestPermissionsHelpersProps {
    user: IUser;
}

export const useDataRequestPermissionsHelpers = ({ user }: IDataRequestPermissionsHelpersProps): IDataRequestPermissionsHelpers => {
    const isOrgMember = (orgId: string, predicate?: (o: IOrganizationRoles) => boolean) => {
        return user?.orgMemberships?.some((o) => o.orgId === orgId && predicate(o));
    };

    const isVivliAdmin = (): boolean => {
        return user?.vivliPermissions?.includes(PermissionsEnum.DrActAdv);
    };

    const isOrgAdmin = (om: IOrganizationRoles): boolean => {
        return om?.vivliPermissions?.includes(PermissionsEnum.ProviderActAdv);
    };

    const canUpload = (request: IDataRequest, study: IRequestedStudy): boolean => {
        if (!request) {
            return false;
        }

        //check for Vivli Admin that happens to have contributor rights
        //by mistake for non-AMR request, don't allow upload
        //TODO PERMFE - redo with permissions+
        if (user.vivliPermissions.includes(PermissionsEnum.DrActAdv) && !request.antimicrobialDetail) {
            return false;
        }

        if (
            (request.status === DataRequestStatusEnum.DUAValidated || request.status === DataRequestStatusEnum.PartiallyFulfilled) &&
            study.studyRequestStatus === StudyRequestStatusEnum.New
        ) {
            //TODO PERMFE - redo with permissions+
            return isOrgMember(study.organizationId, (om) => om.vivliPermissions.includes(PermissionsEnum.ContribChat));
        }

        return false;
    };

    const awaitingApproval = (request: IDataRequest) => {
        switch (request.status) {
            //TODO PERMFE - redo with permissions+
            case DataRequestStatusEnum.AwaitingDataProviderApproval:
                return (
                    user &&
                    request.requiredDataProviderApprovals.some(
                        (dpa: IApprovalDescriptor) =>
                            dpa.status === ApprovalStatusEnum.None &&
                            isOrgMember(dpa.orgId, (om) => om.vivliPermissions.includes(PermissionsEnum.ContribChat))
                    )
                );
            case DataRequestStatusEnum.AwaitingIRPApproval:
                //TODO PERMFE - redo with permissions+
                return (
                    user &&
                    request.requiredIRPApprovals.some(
                        (dpa: IApprovalDescriptor) =>
                            dpa.status === ApprovalStatusEnum.None &&
                            isOrgMember(dpa.orgId, (om) => om.vivliPermissions.includes(PermissionsEnum.ProviderActAdv))
                    )
                );
        }
        return false;
    };

    const canReject = (request: IDataRequest): boolean => {
        //TODO PERMFE - redo with permissions+
        if (user.vivliPermissions.includes(PermissionsEnum.DrActAdv)) {
            return true;
        }

        return awaitingApproval(request);
    };

    const canApprove = (request: IDataRequest): boolean => {
        //TODO PERMFE - redo with permissions+
        if (user.vivliPermissions.includes(PermissionsEnum.DrActAdv)) {
            switch (request.status) {
                case DataRequestStatusEnum.SubmittedToVivli:
                case DataRequestStatusEnum.AwaitingDataProviderApproval:
                case DataRequestStatusEnum.AwaitingIRPApproval:
                case DataRequestStatusEnum.AwaitingDUAValidation:
                case DataRequestStatusEnum.DUAReturnedToRequester:
                    return true;
            }
        }

        return awaitingApproval(request);
    };

    const canSoftReject = (request: IDataRequest): boolean => {
        switch (request.status) {
            case DataRequestStatusEnum.AwaitingDataProviderApproval:
            case DataRequestStatusEnum.AwaitingIRPApproval:
                return canApprove(request);
            case DataRequestStatusEnum.SubmittedToVivli:
            case DataRequestStatusEnum.AwaitingDUAValidation:
            default:
                return false;
        }
    };

    const canEditTeamMember = (teamMember: IResearchTeamMemberDetail): boolean => {
        return !teamMember.isLeadResearcher && !teamMember.isStatistician;
    };

    const canEditEmailField = (teamMember: IResearchTeamMemberDetail): boolean => {
        //TODO PERMFE - redo with permissions+
        if (isVivliAdmin()) {
            return true;
        }

        return canEditTeamMember(teamMember) && !teamMember.hasAccessToVirtualMachineResource && !teamMember.hasBeenInvitedToVMAccess;
    };

    const canEditNonEmailFields = (teamMember: IResearchTeamMemberDetail): boolean => {
        //TODO PERMFE - redo with permissions+
        if (isVivliAdmin()) {
            return true;
        }

        return canEditTeamMember(teamMember);
    };

    return {
        isOrgMember,
        canUpload,
        canReject,
        canSoftReject,
        canApprove,
        awaitingApproval,
        canEditTeamMember,
        canEditEmailField,
        canEditNonEmailFields,
    };
};
